import React, { useCallback, useState, memo } from 'react';
import type { FileInfo } from '../../types/contentBlock';

export interface FileCardProps {
  readonly file: FileInfo;
}

/** 文件扩展名 → 图标 class */
const getFileIcon = (mimeType: string): string => {
  if (/^image\//.test(mimeType)) return 'fa-file-image';
  if (/^video\//.test(mimeType)) return 'fa-file-video';
  if (/^audio\//.test(mimeType)) return 'fa-file-audio';
  if (/pdf/.test(mimeType)) return 'fa-file-pdf';
  if (/html/.test(mimeType)) return 'fa-file-code';
  if (/css/.test(mimeType)) return 'fa-file-code';
  if (/javascript|typescript/.test(mimeType)) return 'fa-file-code';
  if (/json/.test(mimeType)) return 'fa-file-code';
  if (/markdown/.test(mimeType)) return 'fa-file-alt';
  if (/text\//.test(mimeType)) return 'fa-file-alt';
  if (/zip|rar|tar|gz/.test(mimeType)) return 'fa-file-archive';
  if (/word|docx/.test(mimeType)) return 'fa-file-word';
  if (/excel|xlsx|csv/.test(mimeType)) return 'fa-file-excel';
  if (/powerpoint|pptx/.test(mimeType)) return 'fa-file-powerpoint';
  return 'fa-file';
};

/** 文件扩展名 → 显示标签 */
const getFileTypeLabel = (file: FileInfo): string => {
  const ext = file.extension?.replace('.', '').toUpperCase();
  if (ext) return ext;
  const mime = file.mime_type;
  if (/html/.test(mime)) return 'HTML';
  if (/css/.test(mime)) return 'CSS';
  if (/javascript/.test(mime)) return 'JS';
  if (/markdown/.test(mime)) return 'MD';
  if (/json/.test(mime)) return 'JSON';
  return 'FILE';
};

/** 格式化文件大小 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[i]}`;
};

/**
 * 下载文件到本地（fetch → blob → a.download）
 */
const downloadFile = async (url: string, filename: string): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`下载失败: ${response.status}`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();

  // 清理
  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  }, 100);
};

/**
 * 文件卡片组件
 * 显示文件图标、名称、大小，支持下载和预览
 */
export const FileCard: React.FC<FileCardProps> = memo(({ file }) => {
  const [downloading, setDownloading] = useState(false);
  const iconClass = getFileIcon(file.mime_type);
  const typeLabel = getFileTypeLabel(file);
  const sizeText = formatFileSize(file.size);

  const isPreviewable = /^(image|text\/html|application\/pdf)/.test(file.mime_type);

  const handleDownload = useCallback(async (): Promise<void> => {
    if (downloading || !file.url) return;
    setDownloading(true);
    try {
      await downloadFile(file.url, file.filename);
    } catch (error) {
      console.error('[FileCard] 下载失败:', error);
    } finally {
      setDownloading(false);
    }
  }, [downloading, file.url, file.filename]);

  const handlePreview = useCallback((): void => {
    if (file.url) {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    }
  }, [file.url]);

  return (
    <div className="file-card">
      <div className="file-card-icon">
        <i className={`fas ${iconClass}`} />
      </div>
      <div className="file-card-info">
        <span className="file-card-name" title={file.filename}>{file.filename}</span>
        <span className="file-card-meta">{typeLabel} · {sizeText}</span>
      </div>
      <div className="file-card-actions">
        {/* {isPreviewable && (
          <button
            type="button"
            className="file-card-btn"
            onClick={handlePreview}
            title="预览"
          >
            <i className="fas fa-external-link-alt" />
          </button>
        )} */}
        <button
          type="button"
          className="file-card-btn"
          onClick={handleDownload}
          title={downloading ? '下载中...' : '下载'}
          disabled={downloading}
        >
          <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`} />
        </button>
      </div>
    </div>
  );
});

FileCard.displayName = 'FileCard';